import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import {
  useAffiliates,
  useDeleteAffiliate,
  useInviteAffiliate,
  useRestoreAffiliate,
  useSendAffiliateReport,
  useSuspendAffiliate,
} from "@/hooks/useAffiliates";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth, usePermission } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { AffiliateUser } from "@/lib/schema";
import { format } from "date-fns";
import {
  BarChart3,
  Eye,
  FileText,
  MoreHorizontal,
  Plus,
  Search,
  Shield,
  ShieldOff,
  Trash2,
  UserCircle,
  Users as UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

function safeFormatDate(
  value: string | null | undefined,
  pattern: string,
  fallback = "—",
): string {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : format(d, pattern);
}

export default function Affiliates() {
  const [, setLocation] = useLocation();
  const { user: currentUser } = useAuth();
  const canCreate =
    currentUser?.roles &&
    hasPermission(currentUser.roles, "influencer", "create");
  const canSuspend = usePermission("influencer", "suspend");
  const canDelete = usePermission("influencer", "delete_gdpr");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const [actionDialog, setActionDialog] = useState<{
    type: "suspend" | "restore" | "delete" | "inviteInfluencer" | null;
    user: AffiliateUser | null;
  }>({ type: null, user: null });
  const [actionReason, setActionReason] = useState("");

  const { data: affiliates, isLoading: usersLoading } = useAffiliates();

  // Client-side filtering and pagination since API doesn't support it yet
  const searchLower = debouncedSearch.toLowerCase();
  const filteredUsers = (affiliates || []).filter((user) => {
    const matchesSearch =
      searchLower === "" ||
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower);

    const status = user.influencerProfile?.status || "ACTIVE";
    const isDeleted = !!user.deletedAt;
    const isActive = status === "ACTIVE" && !isDeleted;
    const isSuspended = status === "SUSPENDED" && !isDeleted;
    const isDeactivated = status === "REJECTED" && !isDeleted;

    // EVO-102: deleted accounts aren't members; they appear only under the
    // "Deleted" filter (where Restore lives).
    const matchesStatus =
      (statusFilter === "all" && !isDeleted) ||
      (statusFilter === "active" && isActive) ||
      (statusFilter === "suspended" && isSuspended) ||
      (statusFilter === "deactivated" && isDeactivated) ||
      (statusFilter === "deleted" && isDeleted);

    return matchesSearch && matchesStatus;
  });

  const totalUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handleActionSuccess = () => {
    setActionDialog({ type: null, user: null });
    setActionReason("");
  };

  const inviteMutation = useInviteAffiliate(handleActionSuccess);
  const deleteUserMutation = useDeleteAffiliate(handleActionSuccess);
  const restoreUserMutation = useRestoreAffiliate(handleActionSuccess);
  const suspendUserMutation = useSuspendAffiliate(handleActionSuccess);
  const sendReportMutation = useSendAffiliateReport();

  const handleAction = (
    type: "restore" | "delete" | "suspend",
    user: AffiliateUser,
  ) => {
    setActionDialog({ type, user });
  };

  const handleInviteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const name = formData.get("name") as string;

    try {
      await inviteMutation.mutateAsync({
        name,
        email,
      });
      setIsInviteDialogOpen(false);
    } catch {
      // Error toast is handled by the useInviteAffiliate hook
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="px-8 lg:px-12 py-8 lg:py-10">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Affiliates
          </h1>
          <p className="text-muted-foreground mt-1">
            The compassionate voices that nurture your community's growth
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-4">
          <Link href="/affiliates/requests">
            <Card
              className="hover-elevate cursor-pointer border-primary/20"
              data-testid="card-operations-dashboard"
            >
              <CardHeader className="flex flex-row items-center gap-3 p-4">
                <div className="p-2 rounded-md bg-primary/10">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Affiliate Operations
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Monitor affiliate content, track engagement, and manage
                    community activities
                  </p>
                </div>
              </CardHeader>
            </Card>
          </Link>
        </div>

        <section>
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h2 className="text-xl font-serif font-medium text-foreground">
                All Affiliates
              </h2>
              <p className="text-sm text-muted-foreground">
                {statusFilter === "deleted"
                  ? `${totalUsers} deleted`
                  : `${totalUsers} ${totalUsers === 1 ? "member" : "members"} in your community`}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search affiliates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="input-search-affiliates"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="w-[140px]"
                  data-testid="select-affiliate-status"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="deactivated">Deactivated</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
              {canCreate && (
                <Button
                  onClick={() => setIsInviteDialogOpen(true)}
                  data-testid="button-add-affiliate"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Affiliate
                </Button>
              )}
            </div>
          </div>

          {usersLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-3">
                {paginatedUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <UsersIcon className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No members found</p>
                  </div>
                ) : (
                  paginatedUsers.map((user) => {
                    const isDeleted = !!user.deletedAt;
                    const isSuspended =
                      user.influencerProfile?.status === "SUSPENDED" &&
                      !isDeleted;
                    return (
                      <Card
                        key={user.id}
                        className={`hover-elevate transition-all duration-300 ${isDeleted ? "" : "cursor-pointer"}`}
                        onClick={() =>
                          !isDeleted &&
                          setLocation(`/affiliates/${user.id}/profile`)
                        }
                        data-testid={`row-user-${user.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                                <UserCircle className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {user.name || "Anonymous"}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {user.email || "No email"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-6 flex-wrap">
                              <Badge
                                variant={
                                  isDeleted
                                    ? "outline"
                                    : isSuspended
                                      ? "destructive"
                                      : "secondary"
                                }
                                className="capitalize"
                                data-testid={`badge-affiliate-status-${user.id}`}
                              >
                                {isDeleted
                                  ? "Deleted"
                                  : isSuspended
                                    ? "Suspended"
                                    : "Active"}
                              </Badge>
                              <div className="text-center min-w-[90px] hidden md:block">
                                <p className="text-xs text-muted-foreground">
                                  Joined
                                </p>
                                <p className="text-sm text-foreground">
                                  {safeFormatDate(
                                    user.createdAt,
                                    "MMM d, yyyy",
                                  )}
                                </p>
                              </div>

                              <div onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      data-testid={`button-actions-user-${user.id}`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-52"
                                  >
                                    {!isDeleted && (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() =>
                                            setLocation(
                                              `/affiliates/${user.id}/profile`,
                                            )
                                          }
                                        >
                                          <Eye className="mr-2 h-4 w-4" />
                                          View Profile
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() =>
                                            sendReportMutation.mutate({
                                              affiliateId: user.id,
                                            })
                                          }
                                          disabled={
                                            sendReportMutation.isPending
                                          }
                                        >
                                          <FileText className="mr-2 h-4 w-4" />
                                          {sendReportMutation.isPending
                                            ? "Sending..."
                                            : "Send report to mail"}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                      </>
                                    )}
                                    {canSuspend &&
                                      !isSuspended &&
                                      !isDeleted && (
                                        <DropdownMenuItem
                                          onClick={() =>
                                            handleAction("suspend", user)
                                          }
                                          className="text-destructive focus:text-destructive"
                                        >
                                          <Shield className="mr-2 h-4 w-4" />
                                          Suspend Account
                                        </DropdownMenuItem>
                                      )}
                                    {canSuspend &&
                                      (isSuspended || isDeleted) && (
                                        <DropdownMenuItem
                                          onClick={() =>
                                            setActionDialog({
                                              type: "restore",
                                              user,
                                            })
                                          }
                                        >
                                          <ShieldOff className="mr-2 h-4 w-4" />
                                          Restore Account
                                        </DropdownMenuItem>
                                      )}
                                    {canDelete && !isDeleted && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          setActionDialog({
                                            type: "delete",
                                            user,
                                          })
                                        }
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete User (GDPR)
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers}{" "}
                members
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      <ActionDialog
        dialog={actionDialog}
        reason={actionReason}
        onReasonChange={setActionReason}
        onConfirm={() => {
          if (!actionDialog.user) return;
          const userId = actionDialog.user.id;
          switch (actionDialog.type) {
            case "restore":
              restoreUserMutation.mutate(userId);
              break;
            case "suspend":
              suspendUserMutation.mutate(userId);
              break;
            case "delete":
              deleteUserMutation.mutate(userId);
              break;
          }
        }}
        onClose={() => {
          setActionDialog({ type: null, user: null });
          setActionReason("");
        }}
        isPending={
          restoreUserMutation.isPending ||
          suspendUserMutation.isPending ||
          deleteUserMutation.isPending
        }
      />

      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite New Affiliate</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new affiliate (influencer) to the
              platform.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Full Name
              </label>
              <Input
                id="name"
                name="name"
                placeholder="Enter affiliate name"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email Address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="affiliate@example.com"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsInviteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? "Inviting..." : "Invite Affiliate"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActionDialog({
  dialog,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  isPending,
}: {
  dialog: { type: string | null; user: AffiliateUser | null };

  reason: string;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  if (!dialog.type || !dialog.user) return null;

  const configs: Record<
    string,
    {
      title: string;
      description: string;
      confirmText: string;
      variant?: "destructive" | "default";
      showInput: boolean;
      inputLabel?: string;
      inputPlaceholder?: string;
      selectOptions?: { value: string; label: string }[];
    }
  > = {
    suspend: {
      title: "Suspend Account",
      description: `Are you sure you want to suspend ${
        dialog.user.name || "this user"
      }? They will be unable to access the app.`,
      confirmText: "Suspend Account",
      variant: "destructive",
      showInput: true,
      inputLabel: "Reason for suspension (required)",
      inputPlaceholder: "Enter the reason for this suspension...",
    },
    restore: {
      title: "Restore Account",
      description: `Are you sure you want to restore ${
        dialog.user.name || "this user"
      }'s account?`,
      confirmText: "Restore Account",
      variant: "default",
      showInput: true,
      inputLabel: "Restoration note (required)",
      inputPlaceholder: "Enter a note for this restoration...",
    },
    delete: {
      title: "Delete User Data (GDPR)",
      description: `This action is irreversible. All user data will be permanently deleted. Only proceed for GDPR requests, legal compliance, or severe policy violations.`,
      confirmText: "Delete Permanently",
      variant: "destructive",
      showInput: true,
      inputLabel: "Deletion reason",
      selectOptions: [
        { value: "gdpr", label: "GDPR Request" },
        { value: "legal", label: "Legal Compliance" },
        { value: "policy_violation", label: "Policy Violation" },
      ],
    },
  };

  const config = configs[dialog.type];
  if (!config) return null;

  const isValid = !config.showInput || reason.trim().length > 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        {config.showInput && (
          <div className="space-y-2">
            <label className="text-sm font-medium">{config.inputLabel}</label>
            {config.selectOptions ? (
              <Select value={reason} onValueChange={onReasonChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {config.selectOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Textarea
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                placeholder={config.inputPlaceholder}
                rows={3}
              />
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={config.variant}
            onClick={onConfirm}
            disabled={isPending || !isValid}
          >
            {isPending ? "Processing..." : config.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
